import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import { Row, Col } from "antd";

import {
  parseAddress,
  parseDateTime,
  invalidForSelect,
  getConsignmentType,
  parseCashOnDelivery,
} from "../../modules/consignmentFunctions";
import { isUserReader } from "../../modules/session";
import { warningModal } from "../../modules/messages";
import { useUserRole } from "../../modules/redux/session/useAuth";
import { deleteEntity, getEntities } from "../../modules/crudOperations";
import { exportConsignmentDataToExcel } from "../../modules/prints/excelWork";
import SendModal from "../../components/consignmentList/consignmentListTable/sendModal";
import ConsignmentListTable from "../../components/consignmentList/consignmentListTable";
import ConsignmentListFilter from "../../components/consignmentList/consignmentListFilter";
import ConsignmentPrintModal from "../../components/consignmentList/consignmentPrintModal";
import ConsignmentListHeader from "../../components/consignmentList/consignmentListHeader";

function ConsignmentList({ sentList = false, trackingList = false }) {
  const [consignments, setConsignments] = useState();
  const [filterParams, setFilterParams] = useState();
  const [selectedConsignments, setSelectedConsignments] = useState([]);
  const [selectedConsignmentIds, setSelectedConsignmentIds] = useState([]);
  const handleConsignmentsSelect = useCallback((csgs) => {
    setSelectedConsignmentIds(csgs.map((c) => c.key));
    setSelectedConsignments(csgs);
  }, []);
  const handleConsignmentsSelectByIds = useCallback(
    (consignmentIds) => {
      setSelectedConsignmentIds(consignmentIds);
      setSelectedConsignments(
        consignmentIds.length > 0
          ? consignments.filter((c) =>
              consignmentIds.some((id) => id === c.key),
            )
          : [],
      );
    },
    [consignments],
  );

  const loadConsignments = useCallback(
    (parameters) => {
      handleConsignmentsSelect([]);
      getEntities(
        "consignment",
        setConsignments,
        (c) => ({
          key: c.id !== -1 ? c.id : c.externalCode || c.internalCode,
          code: c.code,
          selected: false,
          valid: c.valid,
          state: c.state,
          codes: c.codes,
          internalCode: c.internalCode,
          externalCode: c.externalCode,
          createdByDhl: c.createdByDhl,
          changedByDhl: c.changedByDhl,
          proofOfDeliveryUrl: c.proofOfDeliveryUrl,
          sentByDhl: c.sentByDhl,
          freApiConsignment: c.freApiConsignment,
          insertionDateTime: parseDateTime(c.insertionDateTime),
          customerReference: c.customerReference,
          pickup: parseAddress(c.pickupAddress),
          delivery: parseAddress(c.deliveryAddress),
          countOfCollis: (c.collis && c.collis.length) || 0,
          type: getConsignmentType(c),
          cashOnDelivery: parseCashOnDelivery(c.services.cashOnDelivery),
          sentDateTime: parseDateTime(c.sentDateTime),
          originalSentDateTime: c.sentDateTime,
          insurance: c.services.insurance,
          insuranceDetail: c.services.insuranceDetail,
          consignmentPrice: c.services.consignmentPrice,
          consignmentPriceCurrency:
            c.services.consignmentPriceCurrency &&
            c.services.consignmentPriceCurrency.code,
          lastTrackMessage: c.lastTrackMessage,
          transportPrice: c.transportPrice,
          transportPriceCurrency: c.transportPriceCurrency,
          transportPriceItems: c.transportPriceItems,
          transportPriceDateTime: parseDateTime(c.transportPriceDateTime),
          cancelled: c.cancelled,
          cancellerUser: c.cancellerUser,
          canBeCancelled: c.canBeCancelled,
        }),
        { ...parameters, sent: sentList, track: trackingList },
        true,
        300000,
      );
    },
    [sentList, trackingList, handleConsignmentsSelect],
  );

  const [showFilter, setShowFilter] = useState();
  const handleFilterClick = useCallback(() => {
    setShowFilter(!showFilter);
  }, [showFilter]);

  const [loading, setLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const handleSelectClick = useCallback(() => {
    !loading &&
      consignments &&
      consignments !== "e" &&
      consignments.length > 0 &&
      handleConsignmentsSelect(
        allSelected ? [] : consignments.filter((c) => !invalidForSelect(c)),
      );
  }, [consignments, allSelected, loading, handleConsignmentsSelect]);

  const intl = useIntl();
  const [modalPrintVisible, setModalPrintVisible] = useState();
  const handlePrintClick = useCallback(() => {
    if (selectedConsignmentIds.length > 0) {
      setModalPrintVisible(true);
      setConsignmentsIdsForPrint(selectedConsignmentIds);
    } else {
      warningModal(
        intl.formatMessage({
          id: "consignment.print.noConsignments",
          defaultMessage:
            "Nemáte označené žádné zásilky pro tisk, označte prosím nějaké!",
        }),
      );
    }
  }, [intl, selectedConsignmentIds]);

  const handleExportClick = useCallback(() => {
    if (selectedConsignments.length > 0) {
      exportConsignmentDataToExcel(
        selectedConsignments,
        trackingList,
        sentList,
      );
    } else {
      warningModal(
        intl.formatMessage({
          id: "consignment.export.noConsignments",
          defaultMessage:
            "Nemáte označené žádné zásilky pro export, označte prosím nějaké!",
        }),
      );
    }
  }, [intl, sentList, trackingList, selectedConsignments]);

  const [bulkDelete, setBulkDelete] = useState(false);
  const handleDeleteClick = useCallback(() => {
    if (bulkDelete) {
      const value = intl.formatMessage({
        id: "consignment.genitiv",
        defaultMessage: "zásilky",
      });
      if (selectedConsignmentIds && selectedConsignmentIds.length > 0) {
        deleteEntity(
          `consignment/bulk?${selectedConsignmentIds
            .filter((c) => c.length !== 11)
            .map((c) => `ids=${c}`)
            .join("&")}`,
          "",
          value.charAt(0).toUpperCase() + value.slice(1),
          intl.formatMessage({
            id: "consignment.genitivMultiple",
            defaultMessage: "zásilek",
          }),
          loadConsignments,
          false,
          "y",
        );
      } else {
        warningModal(
          intl.formatMessage(
            {
              id: "consignment.dropConsignments.noConsignments",
              defaultMessage:
                "Nemáte označené žádné {entity} pro smazání, označte prosím nějaké!",
            },
            { entity: value },
          ),
        );
      }
      handleConsignmentsSelect([]);
    }
    setBulkDelete(!bulkDelete);
  }, [
    intl,
    bulkDelete,
    loadConsignments,
    selectedConsignmentIds,
    handleConsignmentsSelect,
  ]);

  const [sendResult, setSendResult] = useState();
  const [modalSendVisible, setModalSendVisible] = useState();
  const [consignmentsIdsForPrint, setConsignmentsIdsForPrint] = useState();

  const handleSendClick = useCallback(() => {
    if (!loading) {
      selectedConsignments.length > 0
        ? setModalSendVisible(true)
        : warningModal(
            intl.formatMessage({
              id: "consignment.send.noConsignments",
              defaultMessage:
                "Nemáte označené žádné zásilky pro odeslání, označte prosím nějaké!",
            }),
          );
    }
  }, [intl, selectedConsignments, loading]);

  const reader = isUserReader(useUserRole(), false);

  useEffect(() => {
    setAllSelected(
      consignments &&
        consignments !== "e" &&
        selectedConsignments.length ===
          consignments.filter((c) => !invalidForSelect(c)).length,
    );
  }, [consignments, selectedConsignments]);

  return (
    <Row>
      <Col>
        <SendModal
          setLoading={setLoading}
          filterParams={filterParams}
          showModal={modalSendVisible}
          setSendResult={setSendResult}
          setConsignments={setConsignments}
          onHideModal={setModalSendVisible}
          loadConsignments={loadConsignments}
          setModalVisible={setModalPrintVisible}
          selectedConsignments={selectedConsignments}
          selectedConsignmentIds={selectedConsignmentIds}
          setConsignmentsIdsForPrint={setConsignmentsIdsForPrint}
        />
        <ConsignmentListHeader
          sentList={sentList}
          showFilter={showFilter}
          bulkDelete={bulkDelete}
          disabledOptions={reader}
          disableSend={bulkDelete}
          allSelected={allSelected}
          trackingList={trackingList}
          onSendClick={handleSendClick}
          onPrintClick={handlePrintClick}
          onSelectClick={handleSelectClick}
          onFilterClick={handleFilterClick}
          onExportClick={handleExportClick}
          onDeleteClick={handleDeleteClick}
        />
        <ConsignmentListFilter
          sentList={sentList}
          showFilter={showFilter}
          trackingList={trackingList}
          onSetShowFilter={setShowFilter}
          setFilterParams={setFilterParams}
          loadConsignments={loadConsignments}
        />
        <ConsignmentListTable
          sentList={sentList}
          bulkDelete={bulkDelete}
          consignments={consignments}
          trackingList={trackingList}
          loadConsignments={loadConsignments}
          selectedConsignmentIds={selectedConsignmentIds}
          setSelectedConsignmentIds={handleConsignmentsSelectByIds}
        />
        <ConsignmentPrintModal
          sendResult={sendResult}
          modalVisible={modalPrintVisible}
          setModalVisible={setModalPrintVisible}
          consignmentIds={consignmentsIdsForPrint}
        />
      </Col>
    </Row>
  );
}

export default ConsignmentList;
